import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@/hooks/use-translation'
import { useSubscriptions } from '@/hooks/use-billing-subscriptions'
import { useShop } from '@/context/shop-context'
import { getCachedPlan } from '@/lib/plan-cache'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

type PlanKey = 'FREE' | 'PLUS' | 'PRO' | 'ENTERPRISE'

const PLAN_LABELS: Record<PlanKey, string> = {
  FREE: 'Free',
  PLUS: 'Plus',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
}

function resolvePlanKey(plan: string | undefined): PlanKey {
  if (plan === 'PLUS' || plan === 'PRO' || plan === 'ENTERPRISE') return plan
  return 'FREE'
}

export function useProfilePlan() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { activeShop } = useShop()
  const shopId =
    activeShop?._id ?? (activeShop as { id?: string } | null)?.id ?? ''
  const { data, isLoading, isFetching } = useSubscriptions()

  const cachedPlan = shopId ? getCachedPlan(shopId) : null
  const resolvedPlan =
    data?.entitlements.plan ?? cachedPlan ?? undefined
  const currentPlan = resolvePlanKey(resolvedPlan)
  const planLabel = t(PLAN_LABELS[currentPlan])
  const showUpgrade = currentPlan !== 'ENTERPRISE'
  const showPlanSkeleton = isLoading && !resolvedPlan

  const goToUpgrade = () => {
    if (!showUpgrade || !shopId) return
    void navigate({ to: '/upgrade-account' })
  }

  return {
    planLabel,
    showPlanSkeleton,
    isFetching,
    cachedPlan,
    showUpgrade,
    shopId,
    goToUpgrade,
    t,
  }
}

export function ProfilePlanCard() {
  const {
    planLabel,
    showPlanSkeleton,
    isFetching,
    cachedPlan,
    showUpgrade,
    shopId,
    goToUpgrade,
    t,
  } = useProfilePlan()

  return (
    <div className='rounded-lg border bg-muted/40 p-3'>
      <p className='text-xs text-muted-foreground'>{t('Current plan')}</p>
      {showPlanSkeleton ? (
        <Skeleton className='mt-1 h-5 w-16' />
      ) : (
        <p
          className={`mt-0.5 truncate text-sm font-semibold ${isFetching && cachedPlan ? 'opacity-80' : ''}`}
        >
          {planLabel}
        </p>
      )}
      {showUpgrade ? (
        <Button
          type='button'
          size='sm'
          className='mt-3 w-full bg-[var(--mp-tomato-500)] hover:bg-[var(--mp-tomato-500)]/90'
          disabled={!shopId || showPlanSkeleton}
          onClick={goToUpgrade}
        >
          {t('Upgrade Now')}
        </Button>
      ) : null}
    </div>
  )
}
